import React, { useState, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import scheduleTemplateService, { type ScheduleVideo, type ScheduleTemplateWithVideos } from '../services/scheduleTemplateService';
import videoService, { type Video } from '../services/videoService';
import importService, { type OneStudioVideo, type OttStudioVideo } from '../services/importService';

interface ScheduleBuilderModalProps {
  templateId: string;
  onClose: () => void;
  onUpdate: () => void;
}

const ItemTypes = {
  VIDEO: 'video',
  SCHEDULE_VIDEO: 'schedule_video',
};

// Draggable video from library
const DraggableVideo: React.FC<{ video: Video }> = ({ video }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.VIDEO,
    item: { video },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <div
      // @ts-ignore - React DnD ref type incompatibility
      ref={drag}
      className={`bg-white border border-gray-300 rounded-lg p-3 cursor-move hover:shadow-md transition-shadow ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        {video.thumbnail_preview_url && (
          <img
            src={video.thumbnail_preview_url}
            alt={video.name}
            className="w-16 h-16 object-cover rounded"
          />
        )}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 truncate">{video.name}</h4>
          <p className="text-xs text-gray-500">
            {Math.floor(video.duration / 60)}:{String(Math.floor(video.duration % 60)).padStart(2, '0')}
          </p>
        </div>
      </div>
    </div>
  );
};

// Helper to get thumbnail URL from 1Studio/OTT-Studio video
const getStudioThumbnailUrl = (video: OneStudioVideo): string | undefined => {
  // If thumbnail_name is already a full URL, use it
  if (video.thumbnail_name?.startsWith('http')) {
    return video.thumbnail_name;
  }
  // Otherwise construct from HLS URL base
  if (video.hls_url) {
    const baseUrl = video.hls_url.substring(0, video.hls_url.lastIndexOf('/'));
    return `${baseUrl}/thumbnail.jpg`;
  }
  return undefined;
};

// Draggable 1Studio video
const DraggableOneStudioVideo: React.FC<{ video: OneStudioVideo }> = ({ video }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.VIDEO,
    item: { oneStudioVideo: video },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const thumbnailUrl = getStudioThumbnailUrl(video);

  return (
    <div
      // @ts-ignore - React DnD ref type incompatibility
      ref={drag}
      className={`bg-white border border-blue-300 rounded-lg p-3 cursor-move hover:shadow-md transition-shadow ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt={video.title || 'Video'}
            className="w-16 h-16 object-cover rounded"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 truncate">{video.title}</h4>
          <p className="text-xs text-gray-500">
            {Math.floor(video.duration / 60)}:{String(Math.floor(video.duration % 60)).padStart(2, '0')}
          </p>
          <span className="inline-block px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded mt-1">
            1Studio
          </span>
        </div>
      </div>
    </div>
  );
};

// Draggable OTT-Studio video
const DraggableOttStudioVideo: React.FC<{ video: OneStudioVideo }> = ({ video }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.VIDEO,
    item: { ottStudioVideo: video },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const thumbnailUrl = getStudioThumbnailUrl(video);

  return (
    <div
      // @ts-ignore - React DnD ref type incompatibility
      ref={drag}
      className={`bg-white border border-purple-300 rounded-lg p-3 cursor-move hover:shadow-md transition-shadow ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        {thumbnailUrl && (
          <img
            src={thumbnailUrl}
            alt={video.title || 'Video'}
            className="w-16 h-16 object-cover rounded"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 truncate">{video.title}</h4>
          <p className="text-xs text-gray-500">
            {Math.floor(video.duration / 60)}:{String(Math.floor(video.duration % 60)).padStart(2, '0')}
          </p>
          <span className="inline-block px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded mt-1">
            OTT-Studio
          </span>
        </div>
      </div>
    </div>
  );
};

// Draggable video in schedule
const ScheduleVideoItem: React.FC<{
  scheduleVideo: ScheduleVideo;
  index: number;
  moveVideo: (dragIndex: number, hoverIndex: number) => void;
  onRemove: () => void;
}> = ({ scheduleVideo, index, moveVideo, onRemove }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.SCHEDULE_VIDEO,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.SCHEDULE_VIDEO,
    hover: (item: { index: number }) => {
      if (item.index !== index) {
        moveVideo(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      // @ts-ignore - React DnD ref type incompatibility
      ref={(node) => drag(drop(node))}
      className={`bg-white border border-gray-300 rounded-lg p-3 cursor-move hover:shadow-md transition-shadow ${
        isDragging ? 'opacity-50' : ''
      }`}
    >
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded font-bold text-sm">
          {scheduleVideo.position}
        </div>
        {scheduleVideo.video_thumbnail && (
          <img
            src={scheduleVideo.video_thumbnail}
            alt={scheduleVideo.video_name || ''}
            className="w-16 h-16 object-cover rounded"
          />
        )}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 truncate">{scheduleVideo.video_name}</h4>
          <p className="text-xs text-gray-500">
            {scheduleVideo.video_duration
              ? `${Math.floor(scheduleVideo.video_duration / 60)}:${String(Math.floor(scheduleVideo.video_duration % 60)).padStart(2, '0')}`
              : 'N/A'}
          </p>
        </div>
        <button
          onClick={onRemove}
          className="text-red-600 hover:text-red-700 p-1"
          title="Remove from schedule"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const ScheduleBuilderModalContent: React.FC<ScheduleBuilderModalProps> = ({ templateId, onClose, onUpdate }) => {
  const [template, setTemplate] = useState<ScheduleTemplateWithVideos | null>(null);
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [scheduleVideos, setScheduleVideos] = useState<ScheduleVideo[]>([]);
  
  // 1Studio video state
  const [videoSource, setVideoSource] = useState<'playout' | 'onestudio' | 'ottstudio'>('playout');
  const [oneStudioChannelId, setOneStudioChannelId] = useState('');
  const [oneStudioVideos, setOneStudioVideos] = useState<OneStudioVideo[]>([]);
  const [oneStudioSearchQuery, setOneStudioSearchQuery] = useState('');
  const [loadingOneStudio, setLoadingOneStudio] = useState(false);
  
  // OTT-Studio video state
  const [ottStudioChannelId, setOttStudioChannelId] = useState('');
  const [ottStudioVideos, setOttStudioVideos] = useState<OttStudioVideo[]>([]);
  const [ottStudioSearchQuery, setOttStudioSearchQuery] = useState('');
  const [loadingOttStudio, setLoadingOttStudio] = useState(false);

  // Fetch template and videos
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [templateData, videosResult] = await Promise.all([
          scheduleTemplateService.get(templateId),
          videoService.listVideos({ page: 1, limit: 100 }),
        ]);
        setTemplate(templateData);
        setScheduleVideos(templateData.videos || []);
        setVideos(videosResult.videos);
      } catch (err: any) {
        alert(err.response?.data?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [templateId]);

  // Load 1Studio videos by channel ID
  const handleLoadOneStudioVideos = async () => {
    const channelId = parseInt(oneStudioChannelId, 10);
    if (isNaN(channelId)) {
      alert('Please enter a valid channel ID');
      return;
    }

    try {
      setLoadingOneStudio(true);
      const result = await importService.getVideos(1000, 0, channelId);
      // Filter out videos without HLS_URL
      const validVideos = result.data.filter(v => v.hls_url && v.hls_url.trim() !== '');
      setOneStudioVideos(validVideos);
      if (validVideos.length === 0) {
        alert('No videos found with HLS URLs for this channel');
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to load 1Studio videos');
    } finally {
      setLoadingOneStudio(false);
    }
  };

  // Load OTT-Studio videos by channel ID
  const handleLoadOttStudioVideos = async () => {
    const channelId = parseInt(ottStudioChannelId, 10);
    if (isNaN(channelId)) {
      alert('Please enter a valid channel ID');
      return;
    }

    try {
      setLoadingOttStudio(true);
      const result = await importService.getOttStudioVideos(1000, 0, channelId);
      // Filter out videos without HLS_URL
      const validVideos = result.data.filter(v => v.hls_url && v.hls_url.trim() !== '');
      setOttStudioVideos(validVideos);
      if (validVideos.length === 0) {
        alert('No videos found with HLS URLs for this channel');
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to load OTT-Studio videos');
    } finally {
      setLoadingOttStudio(false);
    }
  };

  // Filter videos (allow repeats for playout scheduling)
  const availableVideos = videos.filter((video) =>
    video.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter 1Studio videos by search query
  const availableOneStudioVideos = oneStudioVideos.filter((video) =>
    (video.title || '').toLowerCase().includes(oneStudioSearchQuery.toLowerCase())
  );

  // Filter OTT-Studio videos by search query
  const availableOttStudioVideos = ottStudioVideos.filter((video) =>
    (video.title || '').toLowerCase().includes(ottStudioSearchQuery.toLowerCase())
  );

  // Drop zone for adding videos
  const [, drop] = useDrop({
    accept: ItemTypes.VIDEO,
    drop: async (item: { video?: Video; oneStudioVideo?: OneStudioVideo; ottStudioVideo?: OneStudioVideo }) => {
      try {
        const nextPosition = scheduleVideos.length + 1;
        
        // Handle OTT-Studio video
        if (item.ottStudioVideo) {
          const osVideo = item.ottStudioVideo;
          
          if (!osVideo.hls_url) {
            alert('Invalid OTT-Studio video: missing HLS URL');
            return;
          }
          
          const hlsUrl = osVideo.hls_url;
          const baseUrl = hlsUrl.substring(0, hlsUrl.lastIndexOf('/'));
          const highestQualityUrl = `${baseUrl}/1080p/video.m3u8`;
          const thumbnailUrl = getStudioThumbnailUrl(osVideo);
          
          // Create video with source_type='ottstudio'
          const createResponse = await videoService.createVideo({
            name: osVideo.title || 'Untitled',
            description: osVideo.description || '',
            source_type: 'ottstudio',
            remote_url: highestQualityUrl,
            duration: osVideo.duration || 0,
            thumbnail_preview_url: thumbnailUrl,
            ott_studio_video_id: osVideo.id, // Store original OTT-Studio ID
          });
          
          // Add to schedule
          const result = await scheduleTemplateService.addVideo(templateId, {
            video_id: createResponse.id,
            position: nextPosition,
          });
          setTemplate(result.template);
          setScheduleVideos(result.template.videos || []);
          onUpdate();
        }
        // Handle 1Studio video
        else if (item.oneStudioVideo) {
          const osVideo = item.oneStudioVideo;
          
          // For 1Studio videos with HLS ABR streams, use the highest resolution
          // HLS URL points to master playlist, we want the highest quality variant
          if (!osVideo.hls_url) {
            alert('Invalid 1Studio video: missing HLS URL');
            return;
          }
          
          const hlsUrl = osVideo.hls_url;
          
          // Convert master playlist URL to highest quality variant
          // From: https://vz-9dbd7eac-9c6.b-cdn.net/video-id/playlist.m3u8
          // To: https://vz-9dbd7eac-9c6.b-cdn.net/video-id/1080p/video.m3u8 (or highest available)
          const baseUrl = hlsUrl.substring(0, hlsUrl.lastIndexOf('/'));
          const highestQualityUrl = `${baseUrl}/1080p/video.m3u8`;
          const thumbnailUrl = getStudioThumbnailUrl(osVideo);
          
          // First, create the video in our system with source_type='onestudio'
          // Use the highest quality HLS variant as remote_url
          // Store the original 1Studio video ID for CSV playlist imports
          const createResponse = await videoService.createVideo({
            name: osVideo.title || 'Untitled',
            description: osVideo.description || '',
            source_type: 'onestudio',
            remote_url: highestQualityUrl,  // Use 1080p variant
            duration: osVideo.duration || 0,
            thumbnail_preview_url: thumbnailUrl,
            onestudio_video_id: osVideo.id, // Store original 1Studio ID
          });
          
          // Then add it to the schedule
          const result = await scheduleTemplateService.addVideo(templateId, {
            video_id: createResponse.id,
            position: nextPosition,
          });
          setTemplate(result.template);
          setScheduleVideos(result.template.videos || []);
          onUpdate();
        }
        // Handle regular playout video
        else if (item.video) {
          const result = await scheduleTemplateService.addVideo(templateId, {
            video_id: item.video.id,
            position: nextPosition,
          });
          setTemplate(result.template);
          setScheduleVideos(result.template.videos || []);
          onUpdate();
        }
      } catch (err: any) {
        alert(err.response?.data?.message || 'Failed to add video');
      }
    },
  });

  // Reorder videos
  const moveVideo = (dragIndex: number, hoverIndex: number) => {
    const draggedVideo = scheduleVideos[dragIndex];
    const newVideos = [...scheduleVideos];
    newVideos.splice(dragIndex, 1);
    newVideos.splice(hoverIndex, 0, draggedVideo);

    // Update positions
    const updatedVideos = newVideos.map((video, index) => ({
      ...video,
      position: index + 1,
    }));

    setScheduleVideos(updatedVideos);
  };

  // Save reorder
  const handleSaveReorder = async () => {
    try {
      const result = await scheduleTemplateService.reorderVideos(templateId, {
        videos: scheduleVideos.map((sv) => ({
          video_id: sv.video_id,
          position: sv.position,
        })),
      });
      setTemplate(result);
      setScheduleVideos(result.videos || []);
      onUpdate();
      alert('Schedule updated successfully!');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to reorder videos');
    }
  };

  // Remove video from schedule
  const handleRemove = async (videoId: string) => {
    try {
      const result = await scheduleTemplateService.removeVideo(templateId, videoId);
      setTemplate(result);
      setScheduleVideos(result.videos || []);
      onUpdate();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to remove video');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{template?.name}</h2>
          {template?.description && (
            <p className="text-sm text-gray-600 mt-1">{template.description}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 p-2"
          title="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Stats */}
      <div className="flex gap-6 mb-6 bg-blue-50 rounded-lg p-4">
        <div>
          <p className="text-sm text-gray-600">Total Videos</p>
          <p className="text-2xl font-bold text-gray-900">{template?.video_count || 0}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Total Duration</p>
          <p className="text-2xl font-bold text-gray-900">
            {template ? scheduleTemplateService.formatDuration(template.total_duration) : '0s'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex gap-6 overflow-hidden">
        {/* Video Library (Left) */}
        <div className="w-1/2 flex flex-col">
          <div className="mb-4 space-y-3">
            <h3 className="text-lg font-bold text-gray-900">Video Library</h3>
            
            {/* Source selector */}
            <div className="flex gap-2">
              <button
                onClick={() => setVideoSource('playout')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  videoSource === 'playout'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Playout
              </button>
              <button
                onClick={() => setVideoSource('onestudio')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  videoSource === 'onestudio'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                1Studio
              </button>
              <button
                onClick={() => setVideoSource('ottstudio')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  videoSource === 'ottstudio'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                OTT-Studio
              </button>
            </div>
            
            {/* Playout video search */}
            {videoSource === 'playout' && (
              <input
                type="text"
                placeholder="Search videos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
            
            {/* 1Studio channel ID input */}
            {videoSource === 'onestudio' && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Enter Channel ID"
                    value={oneStudioChannelId}
                    onChange={(e) => setOneStudioChannelId(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    onClick={handleLoadOneStudioVideos}
                    disabled={loadingOneStudio}
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {loadingOneStudio ? (
                      <svg className="w-4 h-4 animate-spin" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      'Go'
                    )}
                  </button>
                </div>
                {/* Search box for 1Studio videos */}
                {oneStudioVideos.length > 0 && (
                  <input
                    type="text"
                    placeholder="Search 1Studio videos..."
                    value={oneStudioSearchQuery}
                    onChange={(e) => setOneStudioSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                )}
              </div>
            )}
            
            {/* OTT-Studio channel ID input */}
            {videoSource === 'ottstudio' && (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Enter Channel ID"
                    value={ottStudioChannelId}
                    onChange={(e) => setOttStudioChannelId(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <button
                    onClick={handleLoadOttStudioVideos}
                    disabled={loadingOttStudio}
                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium text-sm transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {loadingOttStudio ? (
                      <svg className="w-4 h-4 animate-spin" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      'Go'
                    )}
                  </button>
                </div>
                {/* Search box for OTT-Studio videos */}
                {ottStudioVideos.length > 0 && (
                  <input
                    type="text"
                    placeholder="Search OTT-Studio videos..."
                    value={ottStudioSearchQuery}
                    onChange={(e) => setOttStudioSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                )}
              </div>
            )}
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-2 pr-2">
            {videoSource === 'playout' && (
              availableVideos.length > 0 ? (
                availableVideos.map((video) => (
                  <DraggableVideo key={video.id} video={video} />
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">
                  {searchQuery ? 'No videos found' : 'No available videos'}
                </p>
              )
            )}
            {videoSource === 'onestudio' && (
              availableOneStudioVideos.length > 0 ? (
                availableOneStudioVideos.map((video) => (
                  <DraggableOneStudioVideo key={video.id} video={video} />
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">
                  {oneStudioVideos.length > 0 
                    ? (oneStudioSearchQuery ? 'No videos match your search' : 'No videos available')
                    : 'Enter a channel ID and click Go to load videos'
                  }
                </p>
              )
            )}
            {videoSource === 'ottstudio' && (
              availableOttStudioVideos.length > 0 ? (
                availableOttStudioVideos.map((video) => (
                  <DraggableOttStudioVideo key={video.id} video={video} />
                ))
              ) : (
                <p className="text-gray-500 text-center py-8">
                  {ottStudioVideos.length > 0 
                    ? (ottStudioSearchQuery ? 'No videos match your search' : 'No videos available')
                    : 'Enter a channel ID and click Go to load videos'
                  }
                </p>
              )
            )}
          </div>
        </div>

        {/* Schedule List (Right) */}
        <div
          // @ts-ignore - React DnD ref type incompatibility
          ref={drop}
          className="w-1/2 flex flex-col bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">
              Schedule ({scheduleVideos.length} videos)
            </h3>
            {scheduleVideos.length > 0 && (
              <button
                onClick={handleSaveReorder}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Save Order
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto space-y-2 pr-2">
            {scheduleVideos.length > 0 ? (
              scheduleVideos.map((scheduleVideo, index) => (
                <ScheduleVideoItem
                  key={scheduleVideo.id}
                  scheduleVideo={scheduleVideo}
                  index={index}
                  moveVideo={moveVideo}
                  onRemove={() => handleRemove(scheduleVideo.video_id)}
                />
              ))
            ) : (
              <div className="text-center py-12">
                <svg
                  className="w-16 h-16 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                <p className="text-gray-500 font-medium">Drag videos here</p>
                <p className="text-sm text-gray-400 mt-1">
                  Drag videos from the library to build your schedule
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
        <p className="text-sm text-gray-600">
          Drag videos from left to right to build your schedule. Reorder by dragging within the schedule.
        </p>
        <button
          onClick={onClose}
          className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
        >
          Done
        </button>
      </div>
    </div>
  );
};

const ScheduleBuilderModal: React.FC<ScheduleBuilderModalProps> = (props) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-7xl w-full h-[90vh] p-6 flex flex-col">
        <DndProvider backend={HTML5Backend}>
          <ScheduleBuilderModalContent {...props} />
        </DndProvider>
      </div>
    </div>
  );
};

export default ScheduleBuilderModal;
